import {
    getTitleForEntity,
    mapBillingUsageEntityByLineItem,
    mapBillingUsageEntityByServiceName
} from 'mappers/billing'
import i18n from 'plugins/i18n'
import {
    isStandAloneTopology,
    isPrimaryReplica,
    getNodeTypeForPricingAPI,
    isProviderAWS
} from 'utils/service'
import { capitalize } from 'helpers'
import t from 'typy'

const SORT_ORDER = {
    Node: 100,
    Storage: 200,
    IOPS: 300,
    Throughput: 400,
    Infrastructure: 500,
    Network: 110,
}

const I18N_MAP = {
    Node: i18n.t('billings.nodes'),
    Storage: i18n.t('billings.storage'),
    Infrastructure: i18n.t('billings.scaleFabric'),
    IOPS: i18n.t('launchService.provisionedIOPs'),
    Throughput: i18n.t('launchService.storageThroughput'),
    Network: i18n.t('billings.secondaryEndpoint'),
}

export function getMultiplierFactor(topology, nodeCount) {
    // Update:: Primary/replica config starts with zero replicas on Portal.
    // so there is no need to add extra 1 for the nodeCount
    // Keeping the function signature intact as to extend in future in case things change
    if (topology) return nodeCount
}

export function getTotalPricingForInstances(
    topology,
    instanceName,
    nodeCount,
    billingData,
    duration = 'hour'
) {
    const instancePrice = billingData.find(
        (instancePricing) => instanceName === instancePricing.name.toLowerCase()
    )
    if (!instancePrice) return null
    const multiplierFactor = getMultiplierFactor(topology, nodeCount)
    return (
        parseFloat(getBillingRateForDuration(duration, instancePrice)) *
        multiplierFactor
    )
}

export function getTotalPricingForStorage(
    topology,
    diskSize,
    nodeCount,
    storageCost,
    duration = 'hour'
) {
    const multiplierFactor = getMultiplierFactor(topology, nodeCount)
    return (
        parseFloat(getBillingRateForDuration(duration, storageCost)) *
        diskSize *
        multiplierFactor
    )
}

export function getTotalPricingForIOPS(
    topology,
    diskSize,
    nodeCount,
    iops,
    iopsCost,
    duration = 'hour'
) {
    const multiplierFactor = getMultiplierFactor(topology, nodeCount)
    return (
        parseFloat(getBillingRateForDuration(duration, iopsCost)) *
        iops *
        diskSize *
        multiplierFactor
    )
}

export function getTotalPricingForScaleFabric(
    topology,
    instanceName,
    nodeCount,
    billingData,
    duration = 'hour'
) {
    const instancePrice = billingData.find(
        (instancePricing) => instanceName === instancePricing.name.toLowerCase()
    )
    if (!instancePrice) return null
    const multiplierFactor = getMultiplierFactor(topology, nodeCount)

    let billingRateForInfra = 0
    if (duration === 'hour') {
        billingRateForInfra = instancePrice.infraPricePerHour
    } else if (duration === 'month') {
        billingRateForInfra = instancePrice.infraPricePerMonth
    } else {
        billingRateForInfra = instancePrice.infraPricePerMin
    }

    return parseFloat(billingRateForInfra) * multiplierFactor
}

function getBillingRateForDuration(duration, billingData) {
    if (duration === 'hour') return billingData.pricePerHour
    if (duration === 'minutes') return billingData.pricePerMin
    else return billingData.pricePerMonth
}

export function prepareUsageByServices(usageData) {
    let usageDataByServiceMap = setupChildrenForAttribute(
        usageData,
        'configurationItem'
    )

    const usageServiceArrays = Object.values(usageDataByServiceMap)
    return usageServiceArrays.map(({ title, children, topology, provider, source, }) => {
        const entitySegregatedChildren = Object.values(
            setupChildrenForAttribute(children, 'category')
        )
        let mappedChildren = entitySegregatedChildren
            .map(entity => ({
                title: I18N_MAP[entity.title],
                children: entity.children.map(mapBillingUsageEntityByLineItem),
                order: SORT_ORDER[entity.title],
            }))
            .map((entity) => ({
                ...entity,
                cost: entity.children.reduce((total, child) => {
                    return child.cost + total
                }, 0),
            })).sort((a, b) => a.order - b.order)

        return {
            title,
            source,
            subTitle: i18n.t('billings.topologyOnProvider', { topology: getTopologyTitle(topology), provider: provider.toUpperCase(), }),
            children: mappedChildren,
            cost: mappedChildren.reduce((total, child) => {
                return child.cost + total
            }, 0),
        }
    })
}

export function prepareUsageByEntities(usageData) {
    let usageDataByEntityMap = setupChildrenForAttribute(
        usageData,
        'category'
    )

    const usageEntityArray = Object.values(usageDataByEntityMap)
    return usageEntityArray.map(({ title, children, }) => {
        children = children.map(usage => ({ ...usage, title: getTitleForEntity(usage), }))

        const entitySegregatedChildren = Object.values(
            setupChildrenForAttribute(children, 'title')
        )
        let mappedChildren = entitySegregatedChildren
            .map(entity => ({
                title: entity.title,
                children: entity.children.map(mapBillingUsageEntityByServiceName),
            }))
            .map((entity) => ({
                ...entity,
                cost: entity.children.reduce((total, child) => {
                    return child.cost + total
                }, 0),
            })).sort((a, b) => a.order - b.order)

        return {
            title: I18N_MAP[title],
            children: mappedChildren,
            cost: mappedChildren.reduce((total, child) => {
                return child.cost + total
            }, 0),
            order: SORT_ORDER[title],
        }
    }).sort((a, b) => a.order - b.order)
}

function setupChildrenForAttribute(usageData, attribute) {
    let hashMap = {}
    usageData.forEach((usage) => {
        const type = usage[attribute]
        if (!hashMap[type]) {
            hashMap[type] = {}
            hashMap[type].title = type
            hashMap[type].provider = usage.provider
            hashMap[type].topology = usage.topology
            hashMap[type].source = usage.source
            hashMap[type].component = usage.component
            hashMap[type].children = []
        }
        hashMap[type].children.push(usage)
    })
    return hashMap
}

function getTopologyTitle(topology) {
    let topologyTitle = ''
    // For SN and Primary/Replica topologies, we need to append the server types
    // when displaying on the billing usage view
    if (isStandAloneTopology(topology) || isPrimaryReplica(topology)) {
        topologyTitle = i18n.t(`serverType.${topology}`) + ' '
    }
    topologyTitle += i18n.t(`topology.${topology}`)
    return topologyTitle
}

export function getMaxScaleRedundancyLabel(nodes, size, isValue) {
    return isValue ? `${capitalize(size)} (x${nodes})` : `x${nodes} ${i18n.t('maxScaleRedundancy')} ${capitalize(size)}`
}

export function getFilteredInstanceCosts(instanceCosts, params) {
    // Filter out the instance costing for
    // the filtered Instances
    const { provider, topology, arch, } = params
    let filteredInstanceCostList = instanceCosts.filter((cost) => {
        const nodeType = getNodeTypeForPricingAPI(topology)
        return (
            cost.topology === topology &&
            cost.provider === provider &&
            cost.node === nodeType
        )
    })
    // Architecture support is only for AWS, not applicable for GCP
    if (isProviderAWS(provider)) {
        filteredInstanceCostList = filteredInstanceCostList.filter(
            (cost) => cost.arch.toLowerCase() === arch
        )
    }
    return filteredInstanceCostList
}
export function getTotalPricingForGP3IOPS(
    topology,
    iops,
    nodeCount,
    iopsCost,
    duration = 'hour'
) {
    const multiplierFactor = getMultiplierFactor(topology, nodeCount)
    return (
        parseFloat(getBillingRateForDuration(duration, iopsCost)) *
        iops *
        multiplierFactor
    )
}
export function getTotalPricingForGP3Throughput(
    topology,
    throughput,
    nodeCount,
    throughputCost,
    duration = 'hour'
) {
    const multiplierFactor = getMultiplierFactor(topology, nodeCount)
    return (
        parseFloat(getBillingRateForDuration(duration, throughputCost)) *
        throughput *
        multiplierFactor
    )
}

export function getPricingForEndpoint(prices, duration = 'hour') {
    return parseFloat(prices[0].unitPrice) * getDurationMultiplier(duration)
}

function getDurationMultiplier(duration) {
    switch (duration) {
    case 'minute':
        return 1
    case 'hour':
        return 60
    case 'month':
        // days * hour * minutes
        return 30 * 24 * 60
    }
}

export function isRemittanceUser(billingRecord={}){
    return t(billingRecord, 'remittanceAccount').safeBoolean
}

export function hasPaymentProfile(paymentProfile={}){
    return t(paymentProfile, 'id').isDefined
}

export function getLegalEntityIdentifier(billingRecord={}){
    return t(billingRecord, 'legalEntityIdentifier').safeString
}

export function isPaymentSetupPendingForUser({
    billingRecord,
    paymentProfile,
}) {
    if (isRemittanceUser(billingRecord)) return false
    return !hasPaymentProfile(paymentProfile)
}
